/**
 * Compare — /friends/:id/compare
 *
 * How you and one friend stand against each other. Nothing else.
 *
 * WHAT THIS PAGE USED TO BE. Compare had grown into two products sharing a
 * scroll: "how do we compare" at the top, and "what am I trading" — Trade
 * Match, shared proposals, the earmarked trade list, both lead panels and their
 * filter wheel — underneath. The trading half is now the Trade Hub
 * (/friends/:id/trades), and everything it took has been REMOVED here rather
 * than left behind, so there is one home for each question and room on this
 * page to build the comparison out properly.
 *
 * WHAT IT IS NOW, top to bottom:
 *   1. The head-to-head — two trainers, and every dex side by side.
 *   2. The history chart: the two of you over time, per dex.
 *   3. Just added — the last three each of you put in the dex.
 *
 * The friend's WISH LIST used to sit here. It moved to the top of the Trade
 * Hub's Potential pane (2026-08-12): a wish list is a shopping list, and it is
 * only actionable next to the leads that can fill it.
 *
 * `?category=` still deep-links a dex (the friend-row pills use it); it now
 * selects which dex the history chart is showing rather than which trade leads
 * are surfaced.
 */
import { useMemo } from "react";
import { useLocation, useRoute } from "wouter";
import { ArrowLeftRight, ArrowRight } from "lucide-react";

import { isDexVariantKey, type DexVariantKey } from "@/components/dexVariantVisuals";
import { parseCompareFilters, queryStringFromLocation } from "@/lib/compareFilters";

import { AppShell } from "@/components/AppShell";
import { DexHistoryChart } from "@/components/trade/DexHistoryChart";
import { DexScoreCard } from "@/components/trade/DexScoreCard";
import { GrindBoard } from "@/components/trade/GrindBoard";
import { TogetherLine } from "@/components/trade/TogetherLine";
import { RecentCatchesPanel } from "@/components/trade/RecentCatchesPanel";
import { TradePageHero } from "@/components/trade/TradePageHero";
import { TrainerVersusPanel } from "@/components/trade/TrainerVersusPanel";
import { TradeSwapIcon } from "@/components/icons/TradeSwapIcon";
import { useFriendIdentity } from "@/lib/useFriendIdentity";

export default function Compare() {
  const [, params] = useRoute<{ id: string }>("/friends/:id/compare");
  const [, setLocation] = useLocation();

  const friendId = params?.id && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const { friend, isLoading: friendLoading } = useFriendIdentity(friendId);

  /**
   * `?category=` deep-link. The friend-row dex pills have always linked here
   * with one, and it used to pre-apply the trade-lead filter; with the trading
   * gone it now chooses which dex the history chart opens on, which is the
   * nearest honest meaning of "show me our Shiny dexes".
   *
   * MUST stay above the early return — a hook below it is how this page threw
   * React #300 twice. See compareBackNavHookOrder.test.tsx.
   */
  const initialChartVariant = useMemo<DexVariantKey>(() => {
    if (typeof window === "undefined") return "pokemon";
    const search = queryStringFromLocation(window.location.hash, window.location.search);
    const category = parseCompareFilters(search, () => false).category;
    if (!category) return "pokemon";
    // The pills speak DexCategory ("main"); the dex axes speak variant keys.
    if (category === "main") return "pokemon";
    return isDexVariantKey(category) ? category : "pokemon";
  }, []);

  if (friendId == null || (!friend && !friendLoading)) {
    return (
      <AppShell title="Compare">
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="v2-neon-card p-8 w-full max-w-xs" data-testid="empty-state-compare">
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: "var(--surface-dark-10)", border: "1.5px solid var(--accent-fill-glow)" }}
            >
              <ArrowLeftRight className="w-6 h-6" style={{ color: "var(--accent-fill)" }} />
            </div>
            <h2 className="v2-display text-base mb-2" style={{ color: "var(--text-ink-bright)" }}>
              Connect with friends to compare Dex
            </h2>
            <p className="text-xs mb-5" style={{ color: "var(--text-ink-faint)" }}>
              Link Dexes with friends and instantly see what each of you has and what you're chasing.
            </p>
            <button
              onClick={() => setLocation("/friends")}
              className="v2-neon-cta w-full py-3 text-sm"
              data-testid="button-go-to-friends"
            >
              Find Friends
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const friendName = friend?.displayName ?? "Trainer";

  return (
    <AppShell pageSpacing>
      <div className="relative z-10 pb-safe">
        <TradePageHero
          eyebrow="Friend Dex"
          title="Compare."
          greenSuffix="Head to head."
          subtitle={`Where you and ${friendName} stand across every dex, and what they're chasing.`}
        />

        <TrainerVersusPanel
          friendName={friendName}
          friendHandle={friend?.handle ?? "Connected trainer"}
          friendMonogram={friend?.monogram ?? "T"}
          friendHue={friend?.hue ?? 0}
          friendAccountId={friendId}
        />

        {/* The answer to the contest above: what the two of you hold BETWEEN
            you. The only figure on this page where the other trainer is an
            asset rather than an opponent, so it sits with the head-to-head
            rather than at the bottom of the page — which is where it was, on a
            Momentum card whose other four lines the chart and the grind board
            had come to say better. */}
        <TogetherLine friendId={friendId} friendName={friendName} />

        {/* The two of you over time. Sits under the head-to-head because it is the
            slow-burn read — the head-to-head says where you stand today, this
            says which way it has been going. */}
        <DexHistoryChart
          friendId={friendId}
          friendName={friendName}
          initialVariant={initialChartVariant}
        />

        {/* Not how many, but how hard. The one contest on this page a trainer
            hundreds behind on raw count can actually win. */}
        <DexScoreCard friendId={friendId} friendName={friendName} />

        {/* Who has been busier, dex by dex. The chart says who is ahead; this
            says who has been putting the work in, which a trainer hundreds
            behind can still win. */}
        <GrindBoard friendId={friendId} friendName={friendName} />

        {/* And what just happened. The chart is the trend; this is the last
            three things either of you actually put in the dex. */}
        <RecentCatchesPanel friendId={friendId} friendName={friendName} />

        {/* Trading is the next question, not the first one — so it is a way out
            of this page rather than a block on it. */}
        <button
          type="button"
          onClick={() => setLocation(`/friends/${friendId}/trades`)}
          className="mt-4 w-full inline-flex items-center justify-between gap-3 rounded-[var(--radius-lg)] px-4 py-3 hover-elevate"
          style={{
            background: "var(--surface-raised)",
            border: "1px solid var(--accent-fill-glow)",
            minHeight: 56,
          }}
          data-testid="button-compare-open-trades"
        >
          <span className="shrink-0" style={{ color: "var(--accent-fill)" }}>
            <TradeSwapIcon className="w-5 h-5" strokeWidth={2} />
          </span>
          <span className="min-w-0 flex-1 text-left">
            <span className="block text-[13px] font-semibold" style={{ color: "var(--text-ink-bright)" }}>
              Trade with {friendName}
            </span>
            <span className="block text-[11px]" style={{ color: "var(--text-ink-faint)" }}>
              Potential swaps, proposals and your trade history.
            </span>
          </span>
          <ArrowRight className="w-4 h-4 shrink-0" style={{ color: "var(--accent-fill)" }} aria-hidden />
        </button>

        {/* Clears the tab bar's protruding centre coin. */}
        <div className="mb-8" aria-hidden />
      </div>
    </AppShell>
  );
}
